import { create } from 'zustand';

import {
  adviseLabOrder,
  assignLabOrder,
  cancelLabOrder,
  collectSpecimen,
  completeLabOrder,
  deleteLabReport,
  downloadLabReport,
  listLabOrdersForVisit,
  listRecentLabOrdersForPatient,
  listLabReports,
  listMyLabQueue,
  recordLabExternal,
  rejectSample,
  replaceLabReport,
  startLabOrder,
  uploadLabReport,
} from '../services/api/labApi';
import { realtimeMiddleware } from './realtimeMiddleware';
import { REALTIME_EVENTS } from '../services/realtime/RealtimeSyncService';

/**
 * Lab order store (Zustand).
 *
 * Holds the technician's lab queue + per-visit lab orders. Lifecycle actions
 * refetch the queue on success; a LAB_ORDER_ASSIGNED realtime event (a
 * receptionist assigning an order, or a walk-in lab visit creating one already
 * assigned) refetches it live so the technician needn't hit Refresh.
 */

// Reconcile the technician's queue from the server, RETRYING past the
// assignee-GSI propagation lag. A single immediate refetch reads back the
// pre-assignment state (the "needs manual refresh" bug); here we keep refetching
// with backoff until the just-assigned order actually lands (or we give up).
// Works without knowing the viewer, so it's the robust fallback for the upsert.
let _labReconcileTimer = null;
function reconcileLabQueue(targetId, attempt = 0) {
  if (_labReconcileTimer) { clearTimeout(_labReconcileTimer); _labReconcileTimer = null; }
  try {
    // eslint-disable-next-line global-require
    const api = require('./labOrderStore').default.getState();
    Promise.resolve(api.fetchMyQueue ? api.fetchMyQueue() : null).then((r) => {
      const list = (r && r.success && Array.isArray(r.data)) ? r.data : null;
      const landed = list && (!targetId || list.some((o) => o.lab_order_id === targetId));
      if (!landed && attempt < 4) {
        _labReconcileTimer = setTimeout(() => reconcileLabQueue(targetId, attempt + 1), 700 * (attempt + 1));
      }
    }).catch(() => {});
  } catch (_e) { /* SSR / HMR */ }
}

// LAB_ORDER_ASSIGNED carries the full written order row. Upsert it straight into
// myQueue (instant, no GSI read-back) when it's assigned to me; drop it if it
// was reassigned away. The retry-reconcile above is the robust backstop and also
// covers the case where the viewer (myMemberId) isn't known yet.
function labOrderAssignedHandler(state, event) {
  const order = event?.payload?.order || null;
  const targetId = order?.lab_order_id || null;
  const myId = state.myMemberId;
  if (order && targetId && myId) {
    const assigneeId = order.assigned_to?.member_id || order.assigned_to_member_id || null;
    const queue = Array.isArray(state.myQueue) ? state.myQueue : [];
    const has = queue.some((o) => o.lab_order_id === targetId);
    if (assigneeId && assigneeId === myId) {
      reconcileLabQueue(targetId);
      return {
        myQueue: has
          ? queue.map((o) => (o.lab_order_id === targetId ? { ...o, ...order } : o))
          : [order, ...queue],
      };
    }
    if (has) {
      reconcileLabQueue(null);
      return { myQueue: queue.filter((o) => o.lab_order_id !== targetId) };
    }
    return {};
  }
  // Unknown viewer / no row in payload — reconcile from the server (retried).
  reconcileLabQueue(targetId);
  return {};
}

// LAB_ORDER_CANCELLED carries just the order id. Drop it from myQueue so a
// cancelled order (self-cancel, or cascaded from a receptionist's visit cancel)
// leaves the technician's lane live, instead of lingering until the next poll.
function labOrderCancelledHandler(state, event) {
  const targetId = event?.payload?.lab_order_id || null;
  if (!targetId) return {};
  const queue = Array.isArray(state.myQueue) ? state.myQueue : [];
  if (!queue.some((o) => o.lab_order_id === targetId)) return {};
  return { myQueue: queue.filter((o) => o.lab_order_id !== targetId) };
}

const useLabOrderStore = create(realtimeMiddleware((set, get) => ({
  myQueue: [],
  byVisit: {},
  // Patient-scoped recent labs (the doctor's Lab summary card): { [patientId]: { orders, hasMore } }
  byPatient: {},
  isLoading: false,
  error: null,
  busyId: null,
  myMemberId: null,

  // Set by the viewing surface (the technician's queue) so a realtime
  // LAB_ORDER_ASSIGNED upsert can tell whether the order belongs to this viewer.
  setMyMemberId: (memberId) => set({ myMemberId: memberId }),

  fetchMyQueue: async () => {
    set({ isLoading: true, error: null });
    const r = await listMyLabQueue();
    if (r.success) set({ myQueue: r.data, isLoading: false });
    else set({ isLoading: false, error: r.error });
    return r;
  },

  fetchForVisit: async (visitId, opts = {}) => {
    const r = await listLabOrdersForVisit(visitId, opts);
    if (r.success) set((s) => ({ byVisit: { ...s.byVisit, [visitId]: r.data } }));
    return r;
  },

  // Patient-scoped recent labs for the doctor's Lab summary card.
  fetchRecentForPatient: async (patientId, opts = {}) => {
    if (!patientId) return { success: false };
    const r = await listRecentLabOrdersForPatient(patientId, opts);
    if (r.success) {
      set((s) => ({ byPatient: { ...s.byPatient, [patientId]: { orders: r.data, hasMore: r.hasMore } } }));
    }
    return r;
  },

  advise: async (body) => {
    const r = await adviseLabOrder(body);
    // advise() is a doctor-context action — refresh this visit's tests AND the
    // patient-scoped Lab summary so both surfaces stay consistent.
    if (r.success && body?.visit_id) await get().fetchForVisit(body.visit_id, { includeAdvised: true });
    if (r.success && body?.patient_id) await get().fetchRecentForPatient(body.patient_id);
    return r;
  },

  // Mark an order "Done (outside)" — a doctor-context completion (the patient got
  // it done elsewhere). File + note both optional. Refresh the doctor surfaces
  // (this visit's tests + the patient Lab summary) so the row flips to completed.
  markDoneOutside: async (id, { note = null, file = null, visitId = null, patientId = null } = {}) => {
    set({ busyId: id });
    const r = await recordLabExternal(id, file, note);
    set({ busyId: null });
    if (r.success) {
      if (visitId) await get().fetchForVisit(visitId, { includeAdvised: true });
      if (patientId) await get().fetchRecentForPatient(patientId);
    }
    return r;
  },

  // Lifecycle actions — refetch the queue on success so the lanes stay correct.
  _run: async (id, fn, body) => {
    set({ busyId: id });
    const r = await fn(id, body);
    set({ busyId: null });
    if (r.success) await get().fetchMyQueue();
    return r;
  },
  collectSpecimen: (id, body) => get()._run(id, collectSpecimen, body),
  start: (id) => get()._run(id, startLabOrder),
  complete: (id, body) => get()._run(id, completeLabOrder, body),
  assign: (id, body) => get()._run(id, assignLabOrder, body),
  cancel: (id, body) => get()._run(id, cancelLabOrder, body),
  // Reject a bad sample (hemolysed / insufficient / wrong tube). Returns the
  // order to payment_cleared for re-collection; the rejection is kept on the
  // order's rejection_history (audit). body = { reason }.
  rejectSample: (id, body) => get()._run(id, rejectSample, body),

  // Completing the test = uploading the result file(s), then marking it
  // complete. Files upload in order; the first failure aborts (the queue is
  // refetched so any reports that did land are reflected).
  uploadReportsAndComplete: async (id, files) => {
    const list = Array.isArray(files) ? files : [files];
    set({ busyId: id });
    for (const file of list) {
      const up = await uploadLabReport(id, file);
      if (!up.success) {
        set({ busyId: null });
        await get().fetchMyQueue();
        return up;
      }
    }
    const r = await completeLabOrder(id, {});
    set({ busyId: null });
    if (r.success) await get().fetchMyQueue();
    return r;
  },

  // Attach more report file(s) to an already-completed order — e.g. after a
  // delete left it with none. Uploads only, no state change.
  attachReports: async (id, files) => {
    const list = Array.isArray(files) ? files : [files];
    set({ busyId: id });
    let last = { success: true };
    for (const file of list) {
      last = await uploadLabReport(id, file);
      if (!last.success) break;
    }
    set({ busyId: null });
    await get().fetchMyQueue();
    return last;
  },

  // Fetch the latest report's bytes as an object-URL for INLINE preview. No
  // window/tab is opened here (window.open after an await is silently blocked by
  // popup filters) — the caller renders the blob in a modal, which is
  // deterministic and surfaces any error. Returns
  // { success, data: { blobUrl, contentType, fileName }, error }.
  fetchLatestReportBlob: async (labOrderId) => {
    const list = await listLabReports(labOrderId);
    if (!list.success || !list.data.length) {
      return { success: false, error: list.error || 'No report found.' };
    }
    const latest = list.data[list.data.length - 1];
    const dl = await downloadLabReport(labOrderId, latest.report_id);
    if (!dl.success) return dl;
    return { success: true, data: { ...dl.data, fileName: latest.file_name } };
  },

  // Replace / delete the latest report (the correction path).
  replaceLatestReport: async (labOrderId, file, reason) => {
    set({ busyId: labOrderId });
    const list = await listLabReports(labOrderId);
    const latest = list.success && list.data.length ? list.data[list.data.length - 1] : null;
    if (!latest) {
      set({ busyId: null });
      return { success: false, error: 'No report to replace.' };
    }
    const r = await replaceLabReport(labOrderId, latest.report_id, file, reason);
    set({ busyId: null });
    if (r.success) await get().fetchMyQueue();
    return r;
  },
  deleteLatestReport: async (labOrderId, reason) => {
    set({ busyId: labOrderId });
    const list = await listLabReports(labOrderId);
    const latest = list.success && list.data.length ? list.data[list.data.length - 1] : null;
    if (!latest) {
      set({ busyId: null });
      return { success: false, error: 'No report to delete.' };
    }
    const r = await deleteLabReport(labOrderId, latest.report_id, reason);
    set({ busyId: null });
    if (r.success) await get().fetchMyQueue();
    return r;
  },
}), {
  storeName: 'lab',
  eventHandlers: {
    [REALTIME_EVENTS.LAB_ORDER_ASSIGNED]: labOrderAssignedHandler,
    [REALTIME_EVENTS.LAB_ORDER_CANCELLED]: labOrderCancelledHandler,
  },
}));

export default useLabOrderStore;
